import type { PostResumo } from "@/lib/blog.functions";
// Portado de /tmp/astro/src/pages/tratamentos/reposicao-hormonal.astro (site Astro).
// Regra CFM: em URL, H1 e título usar "implante hormonal", nunca "chip". O
// termo popular só aparece uma vez no corpo, na FAQ, para explicar.
import { Hero } from "@/components/itv/tratamento/Hero";
import { Pilares } from "@/components/itv/tratamento/Pilares";
import { Dores } from "@/components/itv/tratamento/Dores";
import { CardsCapa } from "@/components/itv/tratamento/CardsCapa";
import { Medico } from "@/components/itv/tratamento/Medico";
import { Faq } from "@/components/itv/tratamento/Faq";
import { Conteudos } from "@/components/itv/tratamento/Conteudos";
import { CtaPratico } from "@/components/itv/tratamento/CtaPratico";

const pilares = [
  { icone: "/icones/search.svg", titulo: "A investigação vem primeiro", texto: "A reposição é indicada quando sintomas e exames apontam na mesma direção. Sintoma isolado não basta." },
  { icone: "/icones/shield.svg", titulo: "Três frentes de tratamento", texto: "Masculina, feminina e implante hormonal: cada frente com sua avaliação, sua página e seu acompanhamento." },
  { icone: "/icones/video-cam.svg", titulo: "Onde e como", texto: "Presencial em Bauru ou por teleconsulta para todo o Brasil. Atendimento particular." },
];

const sintomas = [
  { icone: "/icones/flame.svg", titulo: "Cansaço persistente", texto: "O descanso não resolve e os exames de rotina vieram normais. A rotina básica nem sempre dosa os hormônios relevantes." },
  { icone: "/icones/moon.svg", titulo: "Sono e humor", texto: "Insônia, irritabilidade e desânimo podem ter componente hormonal." },
  { icone: "/icones/activity.svg", titulo: "Queda de libido", texto: "É um sintoma clínico como qualquer outro e merece investigação, não constrangimento." },
  { icone: "/icones/clipboard.svg", titulo: "Sintomas do climatério", texto: "Ondas de calor, alterações do ciclo e mudanças no corpo marcam essa fase e têm tratamento." },
];

const frentes = [
  { img: "/img/hormonal/frente-1.webp", badge: "Reposição masculina", titulo: "Reposição masculina", texto: "Avaliação clínica, sintomas e exames hormonais com indicação médica." },
  { img: "/img/hormonal/frente-2.webp", badge: "Reposição feminina", titulo: "Reposição feminina", texto: "Avaliação dos sintomas e tratamento hormonal individualizado." },
  { img: "/img/hormonal/frente-3.webp", badge: "Implante hormonal", titulo: "Implante hormonal", texto: "Via de administração com liberação contínua e indicação individualizada." },
];

// Menção a "Hospital Albert Einstein" omitida: não consta na lista de credenciais autorizadas.
const paragrafosMedico = [
  "Em reposição hormonal, o que protege o paciente é o critério. O Dr. Thiago tem três especialidades registradas: Nutrologia, Medicina do Esporte e Ortopedia.",
  "Os hormônios são avaliados no contexto do corpo inteiro: sono, treino, metabolismo e composição corporal. Formação pela UNIFESP-EPM, em 2010.",
];

export const faq = [
  { pergunta: "Quando a reposição hormonal é indicada?", resposta: "Quando os sintomas e os exames apontam na mesma direção. Sintoma isolado não basta." },
  { pergunta: "Testosterona baixa sempre precisa de reposição?", resposta: "Não. O exame alterado é um dado, não a conclusão. A indicação depende dos sintomas, da repetição do exame e da investigação do que pode estar por trás da queda." },
  { pergunta: "Implante hormonal é o mesmo que chip?", resposta: "É o mesmo procedimento. \"Chip\" é o termo popular; o nome correto é implante hormonal. Trata-se de uma via de administração com liberação contínua, com indicação individualizada." },
  { pergunta: "A reposição é para sempre?", resposta: "Depende da causa. O acompanhamento reavalia sintomas e exames ao longo do tratamento, e a conduta muda conforme a resposta." },
  { pergunta: "Funciona por teleconsulta?", resposta: "Sim. A avaliação e o acompanhamento acontecem por vídeo, com os exames feitos na sua cidade, para todo o Brasil." },
];


export function SaudeHormonal({ artigos }: { artigos: PostResumo[] }) {
  return (
    <>
      <Hero
        trilha={[{ nome: "Home", href: "/" }, { nome: "Tratamentos", href: "/tratamentos/" }, { nome: "Saúde hormonal" }]}
        sobretitulo="TRATAMENTO"
        linha1="Terapia de"
        linha2="reposição hormonal."
        chamada="Cansaço, queda de libido e sono ruim podem ter origem hormonal. A investigação com exames confirma ou descarta. Quando há deficiência, a reposição é individualizada e acompanhada. Quando não há, o tratamento vai atrás da causa real."
        imagem="/img/hormonal/hero.webp"
      />

      <Pilares sobretitulo="REPOSIÇÃO COM CRITÉRIO" titulo="Investigar antes de repor" itens={pilares} />

      <Dores
        sobretitulo="SEU CORPO AVISA"
        titulo="Sintomas que levam à investigação"
        chamada="Alterações hormonais aparecem aos poucos e costumam ser confundidas com estresse ou cansaço comum. Estes são os sinais mais frequentes no consultório:"
        itens={sintomas}
      />

      <CardsCapa
        sobretitulo="AS TRÊS FRENTES"
        titulo="O tratamento certo para o seu caso"
        chamada="A reposição hormonal aqui se divide em três frentes"
        cards={frentes}
      />

      <Medico paragrafos={paragrafosMedico} />
      <Faq titulo="Dúvidas sobre reposição hormonal" itens={faq} />
      <Conteudos titulo="Continue lendo sobre hormônios" artigos={artigos} />
      <CtaPratico linha1="Comece seu tratamento" linha2="hormonal. Escolha o formato." />
    </>
  );
}
